// Product Master data layer: maps an API product to the form draft and back,
// plus validation, duplicate guards, name conversion and barcode helpers.
// Fields the backend does not store yet (barcode list, unit price list,
// corporate rate, removed images) are sent alongside the existing ones; see
// the "Product Master — Backend Changes" doc for the contract.

export const IMAGE_BASE = `${(window.APP_CONFIG?.BASE_URL || "").replace(/\/+$/, "")}/`;
export const MAX_IMAGES = 8;
export const BATCH = 50;

export const STOCK_TYPES = [
  { value: "1", label: "Stock" },
  { value: "2", label: "Non-Stock" },
  { value: "3", label: "Discontinued" },
];
export const BC_TYPES = ["EAN-13", "UPC-A", "Code 128", "Supplier", "Internal"];
export const PRICE_KEYS = ["cost", "sales", "wholesale", "corporate", "minPrice"];

// ---------- small number helpers ----------
export const num = (v) => {
  const x = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isFinite(x) ? x : 0;
};
export const r2 = (v) => Math.round((v || 0) * 100) / 100;
export const fmt = (v) => (Math.round((Number(v) || 0) * 100) / 100).toFixed(2);
export const grouped = (v) => Number(v || 0).toLocaleString("en-US");

// ---------- reading a product from the list API ----------
const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);

export const pCategory = (p) => pick(p?.categoryID, p?.category?.guid, "");
export const pGroup = (p) => pick(p?.subGroupID, p?.subGroup?.guid, "");
export const pType = (p) => pick(p?.itemTypeID, p?.type?.guid, "");
export const pBrand = (p) => pick(p?.manufactureID, p?.manufacture?.guid, "");
export const pCode = (p) => String(pick(p?.prodCode, p?.code, "") ?? "");

// On-hand quantity, once GetAllByBranch returns it (null until then).
export const pStock = (p) => {
  const v = pick(p?.currentStock, p?.stock, p?.stockQty, p?.closingStock);
  return v === undefined || v === "" ? null : num(v);
};

// Every barcode a product carries: the new list, else the legacy UBC / SBC.
export const pBarcodes = (p) => {
  const list = Array.isArray(p?.productBarcodes) ? p.productBarcodes : null;
  if (list && list.length) {
    return list
      .map((b) => String(pick(b.barcode, b.Barcode, "") ?? "").trim())
      .filter(Boolean);
  }
  return [p?.ubc, p?.sbc, p?.barcode]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i);
};

const taxPct = (taxList, taxGuid) =>
  num(taxList?.find((t) => t.guid === taxGuid)?.percentage);

const imgUrl = (path) =>
  path ? IMAGE_BASE + String(path).trim().replace(/^wwwroot\//, "") : null;

export const blankDraft = (unitList) => {
  const unit = unitList?.[0]?.guid || "";
  return {
    id: 0,
    guid: "",
    name: "",
    nameAr: "",
    code: "",
    brand: "",
    category: "",
    group: "",
    itemType: "",
    stockType: "1",
    unit,
    quickItem: false,
    active: true,
    description: "",
    tax: "",
    taxIncluded: false,
    purchaseTaxIncluded: false,
    minQty: 0,
    maxQty: 0,
    reorderLevel: 0,
    reorderQty: 0,
    units: [{ unit, factor: 1, cost: 0, sales: 0, wholesale: 0, corporate: 0, minPrice: 0 }],
    barcodes: [],
    thumb: null,
    images: [],
    removedImages: [],
  };
};

export const toDraft = (p, { taxList, unitList }) => {
  if (!p) return blankDraft(unitList);
  const tax = pick(p.taxID, p.tax?.guid, "");
  const incl = !!p.isSalesTaxIncludedInPrice;
  // RetailRate is stored net of tax; show what the customer is charged when
  // the price is marked tax-inclusive, so a re-save does not strip tax twice.
  const gross = (v) => (incl ? num(v) * (1 + taxPct(taxList, tax) / 100) : num(v));
  const unit = pick(p.unitID, p.unit?.guid, "");

  const base = {
    unit,
    factor: 1,
    cost: num(p.avgCost),
    sales: r2(gross(p.retailRate)),
    wholesale: num(p.wsRate),
    corporate: num(pick(p.corporateRate, p.wsRate, 0)),
    minPrice: num(p.minPrice),
  };
  const extra = (Array.isArray(p.productUnits) ? p.productUnits : [])
    .filter((u) => !u.isBaseUnit && pick(u.unitID, u.UnitID) !== unit)
    .map((u) => ({
      unit: pick(u.unitID, u.UnitID, ""),
      factor: num(pick(u.conversionFactor, u.ConversionFactor, 1)) || 1,
      cost: num(pick(u.cost, u.Cost)),
      sales: r2(gross(pick(u.salesRate, u.SalesRate))),
      wholesale: num(pick(u.wsRate, u.WSRate)),
      corporate: num(pick(u.corporateRate, u.CorporateRate)),
      minPrice: num(pick(u.minPrice, u.MinPrice)),
    }));

  let barcodes;
  if (Array.isArray(p.productBarcodes) && p.productBarcodes.length) {
    barcodes = p.productBarcodes.map((b) => ({
      value: String(pick(b.barcode, b.Barcode, "")),
      type: pick(b.barcodeType, b.BarcodeType, "EAN-13"),
      unit: pick(b.unitID, b.UnitID, unit),
      pack: num(pick(b.packQty, b.PackQty, 1)) || 1,
      primary: !!pick(b.isPrimary, b.IsPrimary, false),
    }));
    if (!barcodes.some((b) => b.primary)) barcodes[0].primary = true;
  } else {
    barcodes = [p.ubc, p.sbc]
      .map((v) => String(v ?? "").trim())
      .filter(Boolean)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((v, i) => ({
        value: v,
        type: /^\d{13}$/.test(v) ? "EAN-13" : "Supplier",
        unit,
        pack: 1,
        primary: i === 0,
      }));
  }

  const images = String(p.strProdImage || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((path) => ({ path, preview: imgUrl(path) }));

  return {
    id: p.id,
    guid: p.guid,
    name: p.name ?? p.description1 ?? "",
    nameAr: p.shortName ?? "",
    code: pCode(p),
    brand: pBrand(p) || "",
    category: pCategory(p) || "",
    group: pGroup(p) || "",
    itemType: pType(p) || "",
    stockType: String(p.stockTypeID ?? "1"),
    unit,
    quickItem: !!p.isMenuItem,
    active: p.isActive === undefined || p.isActive === null ? true : !!p.isActive,
    description: p.shortDescription ?? "",
    tax,
    taxIncluded: incl,
    purchaseTaxIncluded: !!p.isPurchaseTaxIncludedInPrice,
    minQty: num(p.minQty),
    maxQty: num(p.maxQty),
    reorderLevel: num(p.reorderLevel),
    reorderQty: num(p.reorderQty),
    units: [base, ...extra],
    barcodes,
    thumb: p.thumbImage ? { path: p.thumbImage, preview: imgUrl(p.thumbImage) } : null,
    images,
    removedImages: [],
  };
};

// ---------- building the SaveWithImage request ----------
export const toFormData = (d, { taxList, branch, userId, ProductPos }) => {
  const pct = taxPct(taxList, d.tax);
  const net = (v) => (d.taxIncluded && pct > 0 ? num(v) / (1 + pct / 100) : num(v));
  const base = d.units[0];
  const primary = d.barcodes.find((b) => b.primary) || d.barcodes[0];
  const secondary = d.barcodes.find((b) => b !== primary);

  const o = new ProductPos();
  if (d.guid) {
    o.id = d.id;
    o.guid = d.guid;
  }
  o.ProdCode = d.code.trim();
  o.Description1 = d.name.trim();
  o.ShortName = d.nameAr;
  o.ShortDescription = d.description;
  o.CategoryID = d.category;
  o.SubGroupID = d.group;
  o.ItemTypeID = d.itemType;
  o.StockTypeID = d.stockType;
  o.UnitID = d.unit;
  o.TaxID = d.tax;
  o.TaxPercentage = pct;
  // Brand is optional. The old Add form left it out when none was picked;
  // an empty string can fail binding if the backend column is a Guid.
  o.ManufactureID = String(d.brand ?? "").trim() || undefined;
  o.AvgCost = num(base.cost);
  o.RetailRate = net(base.sales); // stored net of tax, as before
  o.WSRate = num(base.wholesale);
  o.CorporateRate = num(base.corporate);
  o.MinPrice = num(base.minPrice);
  o.MinQty = num(d.minQty);
  o.MaxQty = num(d.maxQty);
  o.ReorderLevel = num(d.reorderLevel);
  o.ReorderQty = num(d.reorderQty);
  o.IsMenuItem = !!d.quickItem;
  o.isActive = !!d.active;
  o.IsSalesTaxIncludedInPrice = !!d.taxIncluded;
  o.IsPurchaseTaxIncludedInPrice = !!d.purchaseTaxIncluded;
  // legacy single-barcode columns keep the sales screens working
  o.Ubc = primary?.value?.trim() || "";
  o.Sbc = secondary?.value?.trim() || "";
  o.ProductBarcodes = d.barcodes.map((b) => ({
    Barcode: String(b.value).trim(),
    BarcodeType: b.type,
    UnitID: b.unit || d.unit,
    PackQty: num(b.pack) || 1,
    IsPrimary: !!b.primary,
  }));
  o.ProductUnits = d.units.map((u, i) => ({
    UnitID: u.unit,
    ConversionFactor: i === 0 ? 1 : num(u.factor),
    IsBaseUnit: i === 0,
    Cost: num(u.cost),
    SalesRate: net(u.sales),
    WSRate: num(u.wholesale),
    CorporateRate: num(u.corporate),
    MinPrice: num(u.minPrice),
  }));
  o.RemovedImages = d.removedImages;
  o.KeepImages = d.images.filter((i) => i.path).map((i) => i.path);
  o.RemoveThumbImage = !d.thumb;
  o.image = "";
  o.productImagesList = [];
  // The old Add / Edit forms never sent ModifiedDate (it went out undefined);
  // the server stamps it. Leave it out so the body matches what worked.
  o.ModifiedDate = undefined;
  o.Branch = branch;
  o.updatedUser = `{${userId?.id}}`;
  if (!d.guid) o.createdUser = `{${userId?.id}}`;

  const fd = new FormData();
  fd.append("product", JSON.stringify(o));
  if (d.thumb?.file) fd.append("thumbimg", d.thumb.file);
  d.images.forEach((i) => i.file && fd.append("productImage", i.file));
  return fd;
};

// ---------- duplicate guards ----------
const same = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

export const findByName = (list, d) =>
  d.name.trim() ? list?.find((p) => p.guid !== d.guid && same(p.name, d.name)) || null : null;

export const findByCode = (list, d) =>
  d.code.trim() ? list?.find((p) => p.guid !== d.guid && same(pCode(p), d.code)) || null : null;

export const findByBarcode = (list, d, value) => {
  const v = String(value ?? "").trim();
  if (!v) return null;
  return list?.find((p) => p.guid !== d.guid && pBarcodes(p).includes(v)) || null;
};

// ---------- validation (first problem wins, with the tab to show) ----------
export const REQUIRED = [
  { f: "name", label: "Name", tab: "profile" },
  { f: "code", label: "Code", tab: "profile" },
  { f: "category", label: "Category", tab: "profile" },
  { f: "group", label: "Group", tab: "profile" },
  { f: "itemType", label: "Item type", tab: "profile" },
  { f: "stockType", label: "Stock type", tab: "profile" },
  { f: "unit", label: "Base unit", tab: "profile" },
  { f: "tax", label: "Tax", tab: "pricing" },
];

export const validate = (d, list, unitName) => {
  const missing = REQUIRED.filter((r) => !String(d[r.f] ?? "").trim());
  if (missing.length) {
    return {
      tab: missing[0].tab,
      fields: missing.map((r) => r.f),
      msg:
        `${missing[0].label} is required` +
        (missing.length > 1 ? ` (${missing.length} fields missing)` : ""),
    };
  }
  if (!(num(d.units[0]?.sales) > 0))
    return { tab: "pricing", msg: "Sales price for the base unit must be more than zero" };
  if (!d.barcodes.length) return { tab: "barcodes", msg: "Add at least one barcode" };

  const dn = findByName(list, d);
  if (dn)
    return {
      tab: "profile",
      fields: ["name"],
      msg: `Name already belongs to ${pCode(dn)} — product names must be unique`,
    };
  const dc = findByCode(list, d);
  if (dc)
    return { tab: "profile", fields: ["code"], msg: `Code ${d.code} already belongs to ${dc.name}` };

  const seen = {};
  for (let i = 0; i < d.barcodes.length; i++) {
    const v = String(d.barcodes[i].value).trim();
    if (!v) return { tab: "barcodes", msg: `Barcode row ${i + 1} is empty` };
    if (seen[v]) return { tab: "barcodes", msg: `Barcode ${v} is listed twice on this product` };
    seen[v] = 1;
    const clash = findByBarcode(list, d, v);
    if (clash)
      return {
        tab: "barcodes",
        msg: `Barcode ${v} already belongs to ${pCode(clash)} — ${clash.name}`,
      };
  }
  for (let j = 1; j < d.units.length; j++) {
    if (!(num(d.units[j].factor) > 0))
      return {
        tab: "pricing",
        msg: `${unitName(d.units[j].unit)} needs a conversion factor greater than zero`,
      };
  }
  return null;
};

// a conversion row priced off the base row; any cell can be overwritten after
export const mkUnitRow = (unit, factor, base, disc = 1) => ({
  unit,
  factor,
  cost: r2(num(base.cost) * factor),
  sales: r2(num(base.sales) * factor * disc),
  wholesale: r2(num(base.wholesale) * factor * disc),
  corporate: r2(num(base.corporate) * factor * disc),
  minPrice: r2(num(base.minPrice) * factor),
});

// ---------- barcodes ----------
// EAN-13 in the in-store range (prefix 2), with a valid check digit.
export const ean13 = () => {
  let d = "2";
  for (let i = 0; i < 11; i++) d += Math.floor(Math.random() * 10);
  let sum = 0;
  for (let j = 0; j < 12; j++) sum += +d[j] * (j % 2 ? 3 : 1);
  return d + ((10 - (sum % 10)) % 10);
};

// Code 128-B bar widths (values 0-106), used to draw printable labels.
const C128 = (
  "212222 222122 222221 121223 121322 131222 122213 122312 132212 221213 221312 231212 112232 122132 " +
  "122231 113222 123122 123221 223211 221132 221231 213212 223112 312131 311222 321122 321221 312212 " +
  "322112 322211 212123 212321 232121 111323 131123 131321 112313 132113 132311 211313 231113 231311 " +
  "112133 112331 132131 113123 113321 133121 313121 211331 231131 213113 213311 213131 311123 311321 " +
  "331121 312113 312311 332111 314111 221411 431111 111224 111422 121124 121421 141122 141221 112214 " +
  "112412 122114 122411 142112 142211 241211 221114 413111 241112 134111 111242 121142 121241 114212 " +
  "124112 124211 411212 421112 421211 212141 214121 412121 111143 111341 131141 114113 114311 411113 " +
  "411311 113141 114131 311141 411131 211412 211214 211232 2331112"
).split(" ");

export const code128Svg = (text, height = 56) => {
  const chars = String(text).replace(/[^\x20-\x7e]/g, "");
  const vals = [104, ...[...chars].map((c) => c.charCodeAt(0) - 32)];
  const check = vals.reduce((s, v, i) => s + v * (i || 1), 0) % 103;
  const seq = [...vals, check, 106];
  let x = 10;
  let bars = "";
  seq.forEach((v) => {
    [...C128[v]].forEach((w, i) => {
      if (i % 2 === 0) bars += `<rect x="${x}" y="0" width="${w}" height="${height}"/>`;
      x += +w;
    });
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${x + 10} ${height}" width="${(x + 10) * 1.4}" height="${height}">${bars}</svg>`;
};

const escHtml = (s) =>
  String(s ?? "").replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));

// Prints one shelf label through a hidden iframe (no pop-up blocker).
export const printBarcodeLabel = ({ value, name, price }) => {
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;width:0;height:0;border:0;right:0;bottom:0";
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><title>Label</title><style>
    @page{size:50mm 30mm;margin:2mm}
    body{margin:0;font-family:Arial,sans-serif;text-align:center}
    .n{font-size:10px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .v{font-family:monospace;font-size:10px;letter-spacing:1px}
    .p{font-size:12px;font-weight:700}
    svg{max-width:100%;height:14mm}
  </style></head><body>
    <div class="n">${escHtml(name)}</div>${code128Svg(value)}
    <div class="v">${escHtml(value)}</div>${price ? `<div class="p">${escHtml(price)}</div>` : ""}
  </body></html>`);
  doc.close();
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }, 150);
};

// ---------- name conversion ----------
// Arabic goes through a small dictionary of pack words, then transliterates;
// Hindi and Malayalam transliterate. translateName() tries the Google API
// first when REACT_APP_TRANSLATION_API_KEY is set.
const AR_DICT = {
  ml: "مل", l: "لتر", ltr: "لتر", g: "جم", gm: "جم", kg: "كجم", m: "متر", pcs: "حبة",
  pc: "حبة", sheets: "ورقة", "pack of": "عبوة", "box of": "علبة", "set of": "طقم",
  classic: "كلاسيك", "value pack": "عبوة اقتصادية", premium: "بريميوم", economy: "اقتصادي",
  "family pack": "عبوة عائلية", assorted: "منوع", blue: "أزرق", red: "أحمر", green: "أخضر",
  large: "كبير", small: "صغير", medium: "وسط", refill: "عبوة تعبئة", "twin pack": "عبوة مزدوجة",
  extra: "إضافي", fresh: "طازج", milk: "حليب", water: "ماء", juice: "عصير", rice: "أرز",
  oil: "زيت", sugar: "سكر", salt: "ملح", tea: "شاي", coffee: "قهوة", bread: "خبز",
  cheese: "جبن", butter: "زبدة", chicken: "دجاج", chocolate: "شوكولاتة", orange: "برتقال",
  apple: "تفاح", mineral: "معدنية", shampoo: "شامبو", soap: "صابون", toothpaste: "معجون أسنان",
};

const TRANSLIT = {
  ar: {
    cons: {
      sh: "ش", ch: "تش", th: "ث", kh: "خ", gh: "غ", ph: "ف", oo: "و", ee: "ي", aa: "ا",
      a: "ا", b: "ب", c: "ك", d: "د", e: "ي", f: "ف", g: "ج", h: "ه", i: "ي", j: "ج", k: "ك",
      l: "ل", m: "م", n: "ن", o: "و", p: "ب", q: "ق", r: "ر", s: "س", t: "ت", u: "و", v: "ف",
      w: "و", x: "كس", y: "ي", z: "ز",
    },
    vow: {},
    matra: null,
  },
  hi: {
    cons: {
      sh: "श", ch: "च", th: "थ", kh: "ख", gh: "घ", ph: "फ",
      b: "ब", c: "क", d: "द", f: "फ", g: "ग", h: "ह", j: "ज", k: "क", l: "ल", m: "म", n: "न",
      p: "प", q: "क", r: "र", s: "स", t: "ट", v: "व", w: "व", x: "क्स", y: "य", z: "ज़",
    },
    vow: { aa: "आ", ee: "ई", oo: "ऊ", a: "अ", e: "ए", i: "इ", o: "ओ", u: "उ" },
    matra: { aa: "ा", ee: "ी", oo: "ू", a: "", e: "े", i: "ि", o: "ो", u: "ु" },
  },
  ml: {
    cons: {
      sh: "ശ", ch: "ച", th: "ത", kh: "ഖ", gh: "ഘ", ph: "ഫ",
      b: "ബ", c: "ക", d: "ദ", f: "ഫ", g: "ഗ", h: "ഹ", j: "ജ", k: "ക", l: "ല", m: "മ", n: "ന",
      p: "പ", q: "ക", r: "ര", s: "സ", t: "ട", v: "വ", w: "വ", x: "ക്സ", y: "യ", z: "സ",
    },
    vow: { aa: "ആ", ee: "ഈ", oo: "ഊ", a: "അ", e: "എ", i: "ഇ", o: "ഒ", u: "ഉ" },
    matra: { aa: "ാ", ee: "ീ", oo: "ൂ", a: "", e: "െ", i: "ി", o: "ൊ", u: "ു" },
  },
};

const translit = (word, lang) => {
  const L = TRANSLIT[lang];
  if (!L) return word;
  const t = word.toLowerCase();
  let out = "";
  let i = 0;
  let afterCons = false;
  while (i < t.length) {
    const two = t.slice(i, i + 2);
    const key = L.cons[two] !== undefined || L.vow[two] !== undefined ? two : t[i];
    if (L.cons[key] !== undefined) {
      out += L.cons[key];
      afterCons = true;
    } else if (L.vow[key] !== undefined) {
      out += afterCons && L.matra ? L.matra[key] || "" : L.vow[key];
      afterCons = false;
    } else {
      out += t[i];
      afterCons = false;
    }
    i += key.length;
  }
  return out;
};

export const localTranslate = (text, lang) => {
  if (!text || !text.trim()) return "";
  const dict = lang === "ar" ? AR_DICT : {};
  const words = text.trim().split(/\s+/);
  const out = [];
  let i = 0;
  while (i < words.length) {
    let matched = false;
    for (let n = Math.min(3, words.length - i); n >= 1; n--) {
      const hit = dict[words.slice(i, i + n).join(" ").toLowerCase()];
      if (hit) {
        out.push(hit);
        i += n;
        matched = true;
        break;
      }
    }
    if (!matched) {
      const w = words[i];
      out.push(/^[0-9][0-9.,/-]*$/.test(w) ? w : translit(w, lang));
      i++;
    }
  }
  return out.join(" ");
};

export const LANGS = [
  { code: "ar", name: "Arabic" },
  { code: "hi", name: "Hindi" },
  { code: "ml", name: "Malayalam" },
];
